import { FC, Dispatch, SetStateAction } from 'react';
import {
  Action,
  DeploymentUnitStatus,
  NotDeployedActions,
} from '@agilelab/plugin-wb-builder-common';
import TabIcon from '@material-ui/icons/SubdirectoryArrowRight';

import {
  DefaultCell,
  TableCellProps,
  TableContentLoader,
  WbSelect,
  WbTable,
  WbTruncatedTypographyWrapper,
} from '@agilelab/plugin-wb-platform';
import { snakeCaseToTitleCase } from '@agilelab/plugin-wb-platform-common';
import { makeStyles, Typography, useTheme } from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { ComponentDependencies } from '../ProvisioningCard/ComponentsTable/ComponentDependencies';
import { DeploymentPreview } from '../types';
import { DeploymentPreviewChip } from './DeploymentPreviewChip';

interface Props {
  rows: Array<DeploymentPreview>;
  loading: boolean;
  enableCleanUpMultiSelection?: boolean;
  setCleanUpPreviews?: Dispatch<SetStateAction<DeploymentPreview[]>>;
}

const useStyles = makeStyles(theme => ({
  selectInput: {
    height: '36px',
    minHeight: 'auto',
    fontSize: theme.typography.body1.fontSize,
  },
  selectTextField: {
    minHeight: 0,
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

export const DeploymentPreviewTable: FC<Props> = ({
  rows,
  loading,
  enableCleanUpMultiSelection,
  setCleanUpPreviews,
}) => {
  const theme = useTheme();
  const classes = useStyles();

  const columns: TableCellProps<DeploymentPreview>[] = [
    {
      field: 'name',
      headerName: 'Name',
      resizable: true,
      fieldRender: field => (
        <div className={classes.nameCell}>
          {field.parent && (
            <TabIcon fontSize="small" htmlColor={theme.palette.grey[500]} />
          )}
          <WbTruncatedTypographyWrapper
            value={field.name}
            style={{ fontWeight: 700 }}
          />
        </div>
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      resizable: true,
      fieldRender: field => <DefaultCell value={field.kind} />,
      formatter: value => snakeCaseToTitleCase(value),
      width: '20%',
    },
    {
      field: 'dependencies',
      headerName: 'Dependencies',
      resizable: true,
      width: '15%',

      fieldRender: field => <ComponentDependencies preview={field} />,
    },
    {
      field: 'currentState',
      headerName: 'Current State',
      resizable: true,
      fieldRender: field => (
        <DeploymentPreviewChip
          status={field.status}
          version={field.statusVersion}
        />
      ),
    },
    {
      field: 'arrow',
      headerName: '',
      uniqueFieldId: 'arrow-decoration',
      resizable: false,
      width: 30,
      fieldRender: () => (
        <ArrowForwardIcon htmlColor={theme.palette.accent.main} />
      ),
    },
    {
      field: 'newState',
      headerName: 'New State',
      resizable: true,
      fieldRender: field => (
        <DeploymentPreviewChip
          status={field.newState as unknown as DeploymentUnitStatus}
        />
      ),
    },
    {
      field: 'action',
      headerName: 'Action',
      resizable: true,
      minWidth: 150,
      fieldRender: field => {
        if (field.parent) {
          return (
            <Typography variant="body2" style={{ fontWeight: 700 }}>
              {field.action && snakeCaseToTitleCase(field.action)}
            </Typography>
          );
        }

        return enableCleanUpMultiSelection ? (
          <WbSelect
            ariaLabel="action"
            value={field.action}
            className={classes.selectTextField}
            onClick={e => e.stopPropagation()}
            onChange={e => {
              const value = e.target.value;
              field.action = value as Action;
              setCleanUpPreviews?.(previews => {
                const indexPreview = previews.findIndex(p => p.id === field.id);
                if (indexPreview !== -1) {
                  previews[indexPreview] = field;
                  return [...previews];
                }
                return [...previews, field];
              });
            }}
            options={
              !field.action || NotDeployedActions.includes(field.action)
                ? NotDeployedActions
                : [field.action]
            }
            getOptionLabel={value => snakeCaseToTitleCase(value)}
            InputProps={{
              className: classes.selectInput,
            }}
          />
        ) : (
          <Typography variant="body2" style={{ fontWeight: 700 }}>
            {field.action && snakeCaseToTitleCase(field.action)}
          </Typography>
        );
      },
    },
  ];

  return (
    <WbTable
      stickyHeader
      components={{
        tableLoader: {
          loading: loading,
          loader: <TableContentLoader rows={10} tableCells={7} />,
        },
        tableContent: {
          columns,
          rows: rows.flatMap(preview => [
            preview,
            ...((preview.components ?? []) as unknown as DeploymentPreview[]),
          ]),
        },
      }}
      styles={{
        container: { height: '100%' },
      }}
    />
  );
};
